import { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { X, Send, Save, RefreshCw, Sparkles, Trash2, FlaskConical } from "lucide-react";

import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import SendTestEmailDialog from "@crm-src/components/send-test-email-dialog";
import { useAuth } from "@crm-src/hooks/useAuth";
import { useToast } from "@crm-src/hooks/use-toast";
import usePermissions from "@crm-src/hooks/usePermissions";
import {
  getColdEmail,
  generateColdEmail,
  updateColdEmail,
  approveColdEmail,
  deleteColdEmail,
  sendColdEmailTest,
  type ColdEmailBatch,
} from "@crm-src/lib/cold-email-api";
import { coldStatusChip, ensureFullBody } from "@crm-src/lib/cold-email-format";

interface Props {
  batchId: number | null;
  open: boolean;
  /** Configured sender, shown in the From row (set in the Generator dialog). */
  fromName: string;
  fromAddress: string;
  onClose: () => void;
  /** Called after any mutation so the list can refresh the row. */
  onChanged: () => void;
}

/**
 * One-click view / generate / edit / send for a single company's cold email.
 * Opens from a queue row so the whole review-and-send loop happens without
 * leaving the list. Generation is synchronous here (a direct crm-api → gateway
 * call), so a company that is "Awaiting draft" turns into a reviewable email in
 * a few seconds. Send is gated to super admins (the CEO).
 */
export function ColdEmailModal({ batchId, open, fromName, fromAddress, onClose, onChanged }: Props): JSX.Element {
  const { toast } = useToast();
  const { isSuperAdmin } = usePermissions();
  const { user } = useAuth();
  const canSend = isSuperAdmin();

  const [batch, setBatch] = useState<ColdEmailBatch | null>(null);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState<"" | "generate" | "save" | "approve" | "delete">("");
  const [confirmSend, setConfirmSend] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  const apply = useCallback((b: ColdEmailBatch): void => {
    setBatch(b);
    setSubject(b.subject ?? "");
    // Always show the whole email (greeting to signature) in the editor, even for
    // a body drafted before that format — ensureFullBody wraps it if needed.
    setBody(b.body ? ensureFullBody(b.body) : "");
  }, []);

  const load = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    try {
      apply(await getColdEmail(batchId));
    } catch {
      toast({ title: "Could not load this cold email", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [batchId, apply, toast]);

  useEffect(() => {
    if (open && batchId) void load();
    if (!open) {
      setBatch(null);
      setBusy("");
    }
  }, [open, batchId, load]);

  const handleGenerate = async (): Promise<void> => {
    if (!batchId) return;
    setBusy("generate");
    try {
      apply(await generateColdEmail(batchId));
      onChanged();
      toast({ title: "Draft ready", description: "Review it, tweak if you like, then send." });
    } catch {
      toast({ title: "Generation failed", description: "The AI service may be busy. Try again.", variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!batchId) return;
    setBusy("save");
    try {
      apply(await updateColdEmail(batchId, { subject, body }));
      onChanged();
      toast({ title: "Draft saved" });
    } catch {
      toast({ title: "Could not save the draft", variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  const handleApprove = async (): Promise<void> => {
    if (!batchId || !batch) return;
    // Save any pending edits first so we send exactly what is on screen.
    if (subject !== (batch.subject ?? "") || body !== (batch.body ?? "")) {
      await updateColdEmail(batchId, { subject, body });
    }
    apply(await approveColdEmail(batchId));
    onChanged();
    toast({ title: "Sending", description: `Delivering to all contacts at ${batch.company_name}.` });
  };

  const handleDelete = async (): Promise<void> => {
    if (!batchId) return;
    setBusy("delete");
    try {
      await deleteColdEmail(batchId);
      onChanged();
      toast({ title: "Deleted", description: "This draft has been removed." });
      onClose();
    } catch {
      toast({ title: "Could not delete this draft", variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  // Send the on-screen draft to one address. The dialog handles error display
  // (it expects this to throw on failure) and closes on success.
  const handleSendTest = async (email: string): Promise<void> => {
    if (!batchId) return;
    await sendColdEmailTest(batchId, {
      email,
      recipient_name: user?.full_name || "",
      subject,
      body,
    });
    toast({ title: "Test email sent", description: `Sent to ${email}.` });
  };

  const chip = batch ? coldStatusChip(batch.status) : null;
  const editable = batch ? batch.status !== "sending" && batch.status !== "sent" : false;
  const hasDraft = Boolean(batch?.subject && batch?.body);
  const canSendNow = editable && hasDraft && (batch?.recipients_count ?? 0) > 0;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 2, pr: 6 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "1.15rem", fontWeight: 700 }} noWrap>
              {batch?.company_name ?? "Cold email"}
            </Typography>
            {batch && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {[batch.industry, batch.company_domain].filter(Boolean).join(" · ") || "Cold outreach"}
                {" · "}
                {batch.recipients_count} contact{batch.recipients_count === 1 ? "" : "s"}
              </Typography>
            )}
          </Box>
          {chip && <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />}
          <IconButton onClick={onClose} sx={{ position: "absolute", top: 8, right: 8 }} size="small">
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : !batch ? (
            <Alert severity="error">This cold email could not be loaded.</Alert>
          ) : (
            <Stack spacing={2}>
              {batch.status === "failed" && batch.generation_error && (
                <Alert severity="error">Last draft failed: {batch.generation_error}</Alert>
              )}
              {batch.status === "sent" && (
                <Alert severity="success">
                  Sent to {batch.sent_count} of {batch.recipients_count} contacts
                  {batch.failed_count > 0 ? ` (${batch.failed_count} failed)` : ""}
                  {batch.approved_by ? ` · approved by ${batch.approved_by}` : ""}.
                </Alert>
              )}
              {batch.status === "sending" && (
                <Alert severity="info">Sending to every contact at {batch.company_name}.</Alert>
              )}

              {!hasDraft ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Sparkles size={28} style={{ opacity: 0.5 }} />
                  <Typography sx={{ mt: 1, fontWeight: 600 }}>No email yet</Typography>
                  <Typography sx={{ color: "text.secondary", mb: 2 }}>
                    Have the AI investigate {batch.company_name} and write a first cold offer.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={busy === "generate" ? <CircularProgress size={18} color="inherit" /> : <Sparkles size={18} />}
                    disabled={busy !== ""}
                    onClick={() => void handleGenerate()}
                  >
                    {busy === "generate" ? `Writing for ${batch.company_name}…` : "Generate email"}
                  </Button>
                </Box>
              ) : (
                <>
                  {/* From / To, like the Email Campaigns editor. From is the
                      configured sender (change it in Generator); To is every
                      contact this email goes to. */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr",
                      columnGap: 2,
                      rowGap: 0.75,
                      alignItems: "start",
                      bgcolor: "surfaceSoft",
                      borderRadius: 1,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>From</Typography>
                    <Typography variant="body2">
                      {fromName} &lt;{fromAddress}&gt;
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>To</Typography>
                    <Box sx={{ maxHeight: 96, overflowY: "auto" }}>
                      {(batch.recipients ?? []).length === 0 ? (
                        <Typography variant="body2">{batch.recipients_count} contacts</Typography>
                      ) : (
                        (batch.recipients ?? []).map((r) => (
                          <Typography key={r.id} variant="body2">
                            {`${r.name ?? ""} ${r.surname ?? ""}`.trim() || r.email}
                            <Typography component="span" sx={{ color: "text.secondary" }}> · {r.email}</Typography>
                            {r.status !== "pending" ? (
                              <Typography component="span" sx={{ color: "text.secondary" }}> · {r.status}</Typography>
                            ) : null}
                          </Typography>
                        ))
                      )}
                    </Box>
                  </Box>

                  <TextField
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={!editable || busy !== ""}
                    fullWidth
                    size="small"
                  />

                  {/* ONE editable email — greeting to signature, exactly what sends.
                      {{full_name}} is the only merge token; the mailer swaps it for
                      each contact's name. What you see is what you get. */}
                  <TextField
                    label="Email"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    disabled={!editable || busy !== ""}
                    fullWidth
                    multiline
                    minRows={12}
                    helperText="This is the exact email. Keep {{full_name}} where the contact's name should go — it becomes their name (e.g. Adam Morrish); use {{first_name}} for just the first name. A small unsubscribe line is added under your signature automatically."
                  />
                </>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          {batch && editable && (
            <>
              {/* LEFT: edit the email (save / regenerate / delete). */}
              {hasDraft && (
                <Button
                  variant="outlined"
                  startIcon={busy === "save" ? <CircularProgress size={16} /> : <Save size={16} />}
                  disabled={busy !== ""}
                  onClick={() => void handleSave()}
                >
                  Save
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={busy === "generate" ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
                disabled={busy !== ""}
                onClick={() => void handleGenerate()}
              >
                {hasDraft ? "Regenerate" : "Generate"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={busy === "delete" ? <CircularProgress size={16} /> : <Trash2 size={16} />}
                disabled={busy !== ""}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>

              <Box sx={{ flex: 1 }} />

              {/* RIGHT: send (test, then the real send). */}
              {hasDraft && (
                <Button
                  variant="outlined"
                  startIcon={<FlaskConical size={16} />}
                  disabled={busy !== ""}
                  onClick={() => setTestOpen(true)}
                >
                  Send test
                </Button>
              )}
              {canSend ? (
                <Button
                  variant="contained"
                  startIcon={<Send size={16} />}
                  disabled={!canSendNow || busy !== ""}
                  onClick={() => setConfirmSend(true)}
                >
                  Approve &amp; send
                </Button>
              ) : (
                <Button variant="contained" startIcon={<Send size={16} />} disabled>
                  CEO approval required
                </Button>
              )}
            </>
          )}
          {batch && !editable && (
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {batch && (
        <ConfirmDialog
          isOpen={confirmSend}
          onClose={() => setConfirmSend(false)}
          title={`Send to all contacts at ${batch.company_name}?`}
          description={`This delivers the email to all ${batch.recipients_count} contact(s). This cannot be undone.`}
          variant="warning"
          confirmLabel="Approve & send"
          onConfirm={async () => {
            await handleApprove();
          }}
        />
      )}

      {batch && (
        <ConfirmDialog
          isOpen={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          title={`Delete the draft for ${batch.company_name}?`}
          description="This removes the queued email and its recipients. You can rebuild the queue later if needed."
          variant="destructive"
          confirmLabel="Delete"
          onConfirm={async () => {
            await handleDelete();
          }}
        />
      )}

      {/* Reuses the exact Email Campaigns test-send dialog; defaults to your own
          address (editable). Sends the on-screen draft to that one address. */}
      <SendTestEmailDialog
        open={testOpen}
        onOpenChange={setTestOpen}
        onSendTest={handleSendTest}
        defaultEmail={user?.email ?? ""}
      />
    </>
  );
}

export default ColdEmailModal;
